import * as DialogPrimitive from '@radix-ui/react-dialog';
import { coincideBusqueda } from '@barbones/shared';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

export interface Comando {
  id: string;
  grupo: string;
  etiqueta: string;
  descripcion?: string;
  palabrasClave?: string;
  icono?: ReactNode;
  accion: () => void;
}

interface Props {
  abierto: boolean;
  onCambiarAbierto: (abierto: boolean) => void;
  /** Comandos locales (pantallas, configuraciones, acciones). */
  comandos: Comando[];
  /** Resultados remotos (clientes, citas, productos…) ya filtrados por el servidor. */
  resultadosRemotos?: Comando[];
  onConsulta?: (consulta: string) => void;
  cargando?: boolean;
}

const MAX_POR_GRUPO = 8;

/** Buscador global de coincidencias (⌘K / Ctrl+K). Ignora acentos y mayúsculas. */
export function CommandPalette({ abierto, onCambiarAbierto, comandos, resultadosRemotos = [], onConsulta, cargando }: Props) {
  const [consulta, setConsulta] = useState('');

  const grupos = useMemo(() => {
    const locales = consulta.trim()
      ? comandos
          .filter((c) => coincideBusqueda(`${c.etiqueta} ${c.descripcion ?? ''} ${c.palabrasClave ?? ''}`, consulta))
          // Primero lo que coincide en el título; el orden original se conserva dentro de cada nivel.
          .map((c, i) => ({ c, i, peso: coincideBusqueda(c.etiqueta, consulta) ? 0 : 1 }))
          .sort((a, b) => a.peso - b.peso || a.i - b.i)
          .map(({ c }) => c)
      : comandos.filter((c) => c.grupo === 'Pantallas');
    const porGrupo = new Map<string, Comando[]>();
    for (const c of [...resultadosRemotos, ...locales]) {
      const lista = porGrupo.get(c.grupo) ?? [];
      if (lista.length < MAX_POR_GRUPO) lista.push(c);
      porGrupo.set(c.grupo, lista);
    }
    return [...porGrupo.entries()];
  }, [comandos, resultadosRemotos, consulta]);

  const cambiarConsulta = (valor: string) => {
    setConsulta(valor);
    onConsulta?.(valor);
  };

  const ejecutar = (comando: Comando) => {
    onCambiarAbierto(false);
    setConsulta('');
    comando.accion();
  };

  return (
    <DialogPrimitive.Root open={abierto} onOpenChange={onCambiarAbierto}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-[#141414]/45 backdrop-blur-[2px]" />
        <DialogPrimitive.Content className="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-flotante animate-entrar">
          <DialogPrimitive.Title className="sr-only">Buscar en el sistema</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Busca pantallas, clientes, citas, productos o configuraciones.
          </DialogPrimitive.Description>
          <Command shouldFilter={false} label="Buscador global" loop>
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Search className="size-4 text-foreground-muted" aria-hidden />
              <Command.Input
                value={consulta}
                onValueChange={cambiarConsulta}
                placeholder="Buscar clientes, citas, productos, pantallas o configuraciones…"
                className="h-12 w-full bg-transparent text-[14.5px] outline-none placeholder:text-foreground-muted/70"
              />
              <kbd className="hidden rounded border border-border px-1.5 text-[11px] text-foreground-muted sm:block">Esc</kbd>
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              {cargando && <Command.Loading className="px-3 py-2 text-[13px] text-foreground-muted">Buscando…</Command.Loading>}
              <Command.Empty className="px-3 py-8 text-center text-[13px] text-foreground-muted">
                Sin coincidencias para “{consulta}”.
              </Command.Empty>
              {grupos.map(([grupo, items]) => (
                <Command.Group
                  key={grupo}
                  heading={grupo}
                  className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-foreground-muted"
                >
                  {items.map((c) => (
                    <Command.Item
                      key={c.id}
                      value={c.id}
                      onSelect={() => ejecutar(c)}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-[14px] data-[selected=true]:bg-accent-soft"
                    >
                      {c.icono && <span className="text-foreground-muted [&_svg]:size-4">{c.icono}</span>}
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{c.etiqueta}</span>
                        {c.descripcion && <span className="truncate text-[12px] text-foreground-muted">{c.descripcion}</span>}
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
